/**
 * Fetches active episode-of-care service types for public intake Step 1
 * via the intake-services-catalog Medplum Bot.
 */

import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';
import { intakeTherapyTypeToServiceTypeCode } from '../../../lib/serviceRequestFhirConstants';

export const INTAKE_SERVICES_CATALOG_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'intake-services-catalog',
} as const;

export interface IntakeServiceSessionDto {
  id: string;
  cost: number | null;
}

export interface IntakeServiceCatalogEntry {
  id: string;
  name: string;
  code: string;
  intakeType: 'both' | 'consultations' | 'intake-sessions';
  sessions: {
    intake: IntakeServiceSessionDto | null;
    consultation: IntakeServiceSessionDto | null;
    standard: IntakeServiceSessionDto | null;
  };
}

export interface IntakeServicesCatalogResult {
  services: IntakeServiceCatalogEntry[];
}

/**
 * Resolve the catalog entry for a selected therapy type. `answers.therapyTypes` are populated
 * directly from each catalog entry's `code` (see StepTherapyType), so we match on `code` first;
 * the mapped-code fallback covers any therapy type sourced as an enum value (e.g. a deep link).
 */
export function findIntakeServiceForTherapyType(
  services: IntakeServiceCatalogEntry[],
  therapyType: string,
): IntakeServiceCatalogEntry | null {
  return (
    services.find((s) => s.code === therapyType) ??
    services.find((s) => s.code === intakeTherapyTypeToServiceTypeCode(therapyType)) ??
    null
  );
}

function getIntakeServicesCatalogBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_INTAKE_SERVICES_CATALOG_BOT_ID?.trim();
  if (id) {
    return id;
  }
  const system =
    import.meta.env.VITE_MEDPLUM_INTAKE_SERVICES_CATALOG_BOT_IDENTIFIER_SYSTEM?.trim()
    ?? INTAKE_SERVICES_CATALOG_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_INTAKE_SERVICES_CATALOG_BOT_IDENTIFIER_VALUE?.trim()
    ?? INTAKE_SERVICES_CATALOG_BOT_IDENTIFIER.value;
  return { system, value };
}

export async function getActiveIntakeServices(
  medplum: MedplumClient,
): Promise<IntakeServicesCatalogResult> {
  return medplum.executeBot(
    getIntakeServicesCatalogBotTarget(),
    {},
    ContentType.JSON,
  ) as Promise<IntakeServicesCatalogResult>;
}
